import { ReactNode } from 'react';
import { Control, Controller, Path } from 'react-hook-form';

import { FormInput } from '@ui/uikit/components/common/FormInput';
import { Minus, Plus } from '@ui/uikit/components/icons/mono';

import { AmountPresetButtons } from './AmountPresetButtons';

type Props<T extends { amount: string }> = {
  name: Path<T>;
  label?: string;
  // @todo Refactoring for accepting any key instead of amount
  control: Control<T>;
  min: number;
  max: number;
  showPresets?: boolean;
  children?: ReactNode;
  renderHintMessage?: (errorMessage?: string) => ReactNode;
  onChangeValue?: (value: string) => void;
};

const formatNumberInput = (value: string, minLimit: number, maxLimit: number): string => {
  if (Number(value) < minLimit && value !== '') {
    return String(minLimit);
  }

  if (Number(value) > maxLimit) {
    return String(Math.max(minLimit, maxLimit));
  }

  return value.replace(/\D/g, '');
};

export const FormAmountInputWithPresets = <T extends { amount: string }>({
  label = '',
  name,
  control,
  min,
  max,
  children,
  showPresets = true,
  renderHintMessage = (error?: string) => error ?? '',
  onChangeValue,
}: Props<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
        const handleChange = (v: string) => {
          const formatedValue = formatNumberInput(v, min, max);
          onChange(formatedValue);

          if (onChangeValue) {
            onChangeValue(formatedValue);
          }
        };

        const handleIncrement = (increment: number) => () => {
          const newValue = String(Number(value) + increment);
          handleChange(newValue);
        };

        return (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <FormInput
                inputRef={ref}
                onValueChange={handleChange}
                value={value}
                label={label}
                required={true}
                data-qa="profile-display-name-input"
                isError={!!fieldState.error}
                hint={renderHintMessage(fieldState.error?.message)}
                afterInput={
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-skeleton-target="base"
                      value={value}
                      className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                      onClick={handleIncrement(-1)}
                    >
                      <Minus className="size-4" />
                    </button>
                    <button
                      type="button"
                      data-skeleton-target="base"
                      value={value}
                      className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                      onClick={handleIncrement(1)}
                    >
                      <Plus className="size-4" />
                    </button>
                  </div>
                }
                {...field}
              />
              {showPresets && (
                <AmountPresetButtons
                  onChange={handleChange}
                  max={max}
                  presetValues={['5', '10', '25']}
                />
              )}
            </div>
            {children}
          </div>
        );
      }}
    />
  );
};
